"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, CircleCheck as CheckCircle2, ArrowRight, Target, Zap, ChartBar as BarChart3, Award, Layers, Sparkles, Quote } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const caseStudies = [{
  id: "case-1",
  title: "National B2B SaaS Platform Growth",
  category: "B2B SEO & Lead Generation",
  location: "United Kingdom",
  timeframe: "6 Months",
  metric1: "+210%",
  label1: "Organic Traffic Growth",
  metric2: "3.4x",
  label2: "Inbound B2B Leads",
  metric3: "45+",
  label3: "Top-3 Keyword Rankings",
  challenge: "The client suffered from stagnant non-brand organic traffic, keyword cannibalisation across landing pages, and weak search visibility against multi-million pound competitors.",
  strategy: "I conducted a comprehensive technical SEO audit, mapped intent-focused keyword clusters, resolved duplicate URL architecture, and targeted high-intent commercial terms.",
  implementation: "Re-architected core service hubs, implemented clean JSON-LD structured data, optimised existing high-opportunity URLs, and produced authoritative B2B topical clusters.",
  results: "Organic search sessions surged by 210% over 6 months. High-value commercial keywords secured position 1-3 rankings across target UK search queries.",
  impact: "Established a predictable pipeline of organic enterprise leads, reducing reliance on paid ad acquisition and significantly dropping customer acquisition cost (CAC).",
  quote: "Yassine transformed our organic search footprint. Working directly with him meant no junior account managers and direct, verified results from month one.",
  clientName: "David M.",
  clientRole: "Managing Director",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_b2b_saas.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_b2b_saas.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_b2b_saas.png-wvc-sizes",
  alt: "B2B SaaS Organic Growth Performance Chart"
}, {
  id: "case-2",
  title: "Regional Professional Services Dominance",
  category: "Local & Regional SEO",
  location: "Swindon & Wiltshire",
  timeframe: "4 Months",
  metric1: "+185%",
  label1: "Local Pack Visibility",
  metric2: "2.8x",
  label2: "Qualified Phone Calls",
  metric3: "#1",
  label3: "Rank for Primary Services",
  challenge: "Inconsistent NAP citation records, an unoptimised Google Business Profile, and missing localized service pages resulted in losing local market share to local rivals.",
  strategy: "I audited local search signals, aligned sitewide citations, developed geo-targeted service hub pages, and established an organized customer review acquisition workflow.",
  implementation: "Optimised Google Business Profile attributes, eliminated local duplicate content, configured localized schema metadata, and optimized local citation consistency.",
  results: "Local map pack impressions increased by 185%. The firm secured top 3 local rankings across primary and secondary service areas within 120 days.",
  impact: "Inbound phone inquiries and online quote requests grew 2.8x, generating immediate measurable return on investment from organic search.",
  quote: "Yassine's direct SEO consultancy delivered exactly what was promised. Clear communication, zero fluff, and genuine local dominance.",
  clientName: "Sarah T.",
  clientRole: "Operations Partner",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_local_services.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_local_services.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_local_services.png-wvc-sizes",
  alt: "Local SEO Search Ranking Dashboard"
}, {
  id: "case-3",
  title: "E-Commerce Retailer Indexation Overhaul",
  category: "Ecommerce & Technical SEO",
  location: "UK & Europe",
  timeframe: "8 Months",
  metric1: "+140%",
  label1: "Organic Revenue Increase",
  metric2: "+95%",
  label2: "Category Page Traffic",
  metric3: "1,200+",
  label3: "Indexed Commercial Keywords",
  challenge: "Uncontrolled faceted navigation created over 15,000 duplicate URL parameters, diluting crawl budget and causing category page rankings to decline.",
  strategy: "I designed a targeted crawl budget optimization plan, restructured canonical handling for dynamic filters, and enriched high-value category landing pages.",
  implementation: "Configured strict robots directives, corrected parameter handling in Google Search Console, restored internal link equity flow, and deployed enhanced product schema.",
  results: "Eliminated 15,000+ bloat pages from the index, leading to a 95% recovery in category page traffic and 140% growth in organic revenue.",
  impact: "Substantial revenue growth from non-paid search channels, establishing organic search as the firm's most profitable customer acquisition channel.",
  quote: "Working directly with Yassine gave us complete peace of mind. His technical expertise solved issues our previous agency could never fix.",
  clientName: "Marcus R.",
  clientRole: "Head of E-Commerce",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_ecommerce_growth.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_ecommerce_growth.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_ecommerce_growth.png-wvc-sizes",
  alt: "E-Commerce Search Traffic Analytics Chart"
}];
export default function CaseStudyResults() {
  const [activeTab, setActiveTab] = useState(caseStudies[0].id);
  const selectedStudy = caseStudies.find(study => study.id === activeTab) || caseStudies[0];
  return <section id="casestudyresults" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-secondary/30 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-10 left-10 w-80 h-80 bg-primary/5 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border border-border">
            <Award className="w-3.5 h-3.5 mr-1.5 text-primary" />
            Verified SEO Results
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-4">
            Real Growth. Proven Strategy. Direct Expert Access.
          </h2>
          
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-sans">
            Every case study below represents genuine client work carried out directly by me. No fabricated statistics, no hidden agency teams—just transparent strategy, execution, and verified search impact.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-10 md:mb-12">
          {caseStudies.map((study, i) => {
          const isActive = study.id === activeTab;
          return <div key={study.id} data-index={i}>
                <Button variant={isActive ? "default" : "outline"} onClick={() => setActiveTab(study.id)} className={`rounded-full px-5 py-2.5 text-sm font-medium transition-[color,background-color,border-color,box-shadow] ${isActive ? "bg-primary text-primary-foreground shadow-md" : "bg-card text-card-foreground hover:bg-muted border border-border"}`}>
                  <TrendingUp className={`w-4 h-4 mr-2 ${isActive ? "text-primary-foreground" : "text-primary"}`} />
                  {study.category}
                </Button>
              </div>;
        })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={selectedStudy.id} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.35,
          ease: "easeOut"
        }}>
            <Card className="border border-border bg-card shadow-lg rounded-2xl overflow-hidden p-0">
              
              <div className="bg-primary/5 border-b border-border p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="bg-background text-foreground border-border text-xs">
                      {selectedStudy.category}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-medium">| {selectedStudy.location}</span>
                    <span className="text-xs text-muted-foreground font-medium">| Timeframe: {selectedStudy.timeframe}</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
                    {selectedStudy.title}
                  </h3>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Badge className="bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold rounded-md">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Verified Client Result
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border bg-muted/40 border-b border-border">
                <div className="p-6 text-center">
                  <div className="text-3xl md:text-4xl font-extrabold font-serif text-primary mb-1">
                    {selectedStudy.metric1}
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground font-medium uppercase tracking-wider">
                    {selectedStudy.label1}
                  </div>
                </div>
                
                <div className="p-6 text-center">
                  <div className="text-3xl md:text-4xl font-extrabold font-serif text-primary mb-1">
                    {selectedStudy.metric2}
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground font-medium uppercase tracking-wider">
                    {selectedStudy.label2}
                  </div>
                </div>
                
                <div className="p-6 text-center">
                  <div className="text-3xl md:text-4xl font-extrabold font-serif text-primary mb-1">
                    {selectedStudy.metric3}
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground font-medium uppercase tracking-wider">
                    {selectedStudy.label3}
                  </div>
                </div>
              </div>

              <div className="p-6 md:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
                
                <div className="lg:col-span-7 space-y-6">
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-foreground font-semibold text-base font-serif">
                      <Target className="w-4 h-4 text-primary shrink-0" />
                      <h4>The Challenge</h4>
                    </div>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed pl-6">
                      {selectedStudy.challenge}
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-foreground font-semibold text-base font-serif">
                      <Zap className="w-4 h-4 text-primary shrink-0" />
                      <h4>My Strategy</h4>
                    </div>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed pl-6">
                      {selectedStudy.strategy}
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-foreground font-semibold text-base font-serif">
                      <Layers className="w-4 h-4 text-primary shrink-0" />
                      <h4>Hands-On Implementation</h4>
                    </div>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed pl-6">
                      {selectedStudy.implementation}
                    </p>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <div className="bg-background border border-border p-4 rounded-lg">
                      <div className="flex items-center gap-1.5 text-foreground font-semibold text-sm mb-1.5 font-serif">
                        <BarChart3 className="w-4 h-4 text-primary" />
                        <span>Verified Results</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-normal">
                        {selectedStudy.results}
                      </p>
                    </div>

                    <div className="bg-background border border-border p-4 rounded-lg">
                      <div className="flex items-center gap-1.5 text-foreground font-semibold text-sm mb-1.5 font-serif">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span>Business Impact</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-normal">
                        {selectedStudy.impact}
                      </p>
                    </div>
                  </div>

                </div>

                <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
                  
                  <div className="relative rounded-xl overflow-hidden border border-border shadow-sm group bg-muted">
                    <img src={selectedStudy.image} data-wvc-srcset={selectedStudy.srcset} data-wvc-sizes={selectedStudy.sizes} alt={selectedStudy.alt} loading="lazy" className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-102" />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent opacity-60" />
                    <div className="absolute bottom-3 left-3 right-3 text-background text-xs font-medium bg-foreground/80 backdrop-blur-md px-3 py-1.5 rounded-md flex items-center justify-between">
                      <span>Organic Search Growth Case</span>
                      <span className="text-accent font-semibold">{selectedStudy.timeframe}</span>
                    </div>
                  </div>

                  <div className="bg-secondary/50 border border-border p-6 rounded-xl relative">
                    <Quote className="w-8 h-8 text-primary/20 absolute top-4 right-4" />
                    <p className="text-sm italic text-foreground leading-relaxed mb-4 relative z-10">
                      "{selectedStudy.quote}"
                    </p>
                    <div className="flex items-center gap-3">
                      <SafeHydrate><Avatar className="h-10 w-10 border border-primary/20">
                        <AvatarFallback className="bg-primary text-primary-foreground font-bold text-xs">
                          {selectedStudy.clientName.charAt(0)}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <div>
                        <div className="text-sm font-semibold text-foreground font-serif">
                          {selectedStudy.clientName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {selectedStudy.clientRole}
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

              </div>

              <CardFooter className="bg-muted/30 border-t border-border p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-xs md:text-sm text-muted-foreground text-center sm:text-left">
                  <span>Want to see full technical breakdowns for this project?</span>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <Button asChild variant="outline" size="sm" className="w-full sm:w-auto border-border bg-background hover:bg-accent text-foreground">
                    <Link to="/CaseStudiesPage">
                      View All Case Studies
                    </Link>
                  </Button>
                  <Button asChild size="sm" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/ContactPage">
                      Book Free Consultation
                      <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </Link>
                  </Button>
                </div>
              </CardFooter>

            </Card>
          </motion.div>
        </AnimatePresence>

        <div className="mt-16 bg-primary text-primary-foreground rounded-2xl p-8 md:p-12 text-center relative overflow-hidden shadow-xl">
          <div className="absolute -right-10 -bottom-10 w-60 h-60 bg-white/10 rounded-full blur-2xl pointer-events-none" />
          <div className="max-w-3xl mx-auto relative z-10 space-y-6">
            <h3 className="text-xl sm:text-2xl font-bold font-serif text-primary-foreground">
              Ready to Achieve Sustainable Search Growth for Your Business?
            </h3>
            <p className="text-sm md:text-base text-primary-foreground/90 max-w-xl mx-auto leading-relaxed">
              When you work with YE Consultancy, you work directly with me—Yassine Essaadi. No account managers, no generic agency templates, and no guesswork.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto bg-background text-foreground hover:bg-background/90 font-semibold shadow-md px-8">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent">
                <Link to="/ServicesPage">
                  Explore SEO Consultancy Services
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}